import { rumVital } from '@garner-health/lib-ui-real-user-monitoring';
import { useCallback, useEffect, useRef } from 'react';

export type UseRumDurationVitalParams = {
  /** Whether to start the vital on mount. Defaults to true. */
  startOnMount?: boolean;
  /** The name of the vital to track. */
  vitalName: string;
};

/**
 * Tracks a custom Datadog RUM duration vital for an app-specific, user-perceived span (e.g. search
 * submit → results, page mount → data loaded). This complements RUM's automatic resource timing,
 * which only measures individual network requests, not multi-step or render-inclusive flows.
 *
 * `startVital`/`stopVital` are idempotent within a single mount: `startVital` no-ops if the vital is
 * already running and `stopVital` no-ops if it was never started, so wiring them to a `loading` flag
 * (start on mount, stop when loaded) is safe and never emits a zero-length vital by accident.
 *
 * Datadog forwarding is a safe no-op until `initDatadog()` has run.
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#create-custom-performance-metrics
 */
export function useRumDurationVital({ startOnMount = true, vitalName }: UseRumDurationVitalParams) {
  const hasStartedRef = useRef(false);

  const startVital = useCallback(() => {
    if (!hasStartedRef.current) {
      rumVital.startDurationVital(vitalName);
      hasStartedRef.current = true;
    }
  }, [vitalName]);

  const stopVital = useCallback(() => {
    if (hasStartedRef.current) {
      rumVital.stopDurationVital(vitalName);
      hasStartedRef.current = false;
    }
  }, [vitalName]);

  useEffect(() => {
    if (startOnMount) startVital();
    return stopVital;
  }, [startOnMount, startVital, stopVital]);

  return { startVital, stopVital };
}

/** Times a load window: runs the vital while `isLoading` is true and stops it when false. */
export function useRumLoadVital(vitalName: string, isLoading: boolean): void {
  const { startVital, stopVital } = useRumDurationVital({ startOnMount: false, vitalName });

  useEffect(() => {
    if (isLoading) startVital();
    else stopVital();
  }, [isLoading, startVital, stopVital]);
}
